import React, { useState, useEffect, useMemo } from 'react'
import { 
  Plus, ShoppingCart, CreditCard, User, Search,
  Calculator, Receipt, FileText, QrCode,
  Trash2, Edit, Check, X, Download, History
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'

import { useToast } from '../components/Toast'
import { Cliente, Produto } from '../types'
import vendasService, { 
  VendaFormData,
  ItemVendaFormData,
  PagamentoVendaFormData,
  formasPagamento,
  tiposDocumento,
  formatarValor,
  formatarQuantidade,
  getFormaPagamentoConfig
} from '../services/vendas'
import produtosService from '../services/produtos'
import clientesService from '../services/clientes'
import apiService from '../services/api'
import PagamentoModal from '../components/vendas/PagamentoModal'
import QRScanner from '../components/QRScanner'

interface ItemCarrinho extends ItemVendaFormData {
  produto: Produto
  subtotal: number
  valorDesconto: number
  total: number
}

const VendasPage: React.FC = () => {
  const { success, error } = useToast()
  const queryClient = useQueryClient()

  // Estados principais
  const [carrinho, setCarrinho] = useState<ItemCarrinho[]>([])
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null)
  const [buscarCliente, setBuscarCliente] = useState('')
  const [buscarProduto, setBuscarProduto] = useState('')
  const [descontoGeral, setDescontoGeral] = useState(0)
  const [observacoes, setObservacoes] = useState('')
  const [tipoDocumento, setTipoDocumento] = useState<'RECIBO' | 'FATURA'>('RECIBO')

  // Estados dos modais
  const [showClienteModal, setShowClienteModal] = useState(false)
  const [showPagamentoModal, setShowPagamentoModal] = useState(false)
  const [showScannerModal, setShowScannerModal] = useState(false)
  const [showVendasRecentesModal, setShowVendasRecentesModal] = useState(false)

  // Estados do pagamento
  const [pagamentos, setPagamentos] = useState<PagamentoVendaFormData[]>([])

  // Queries
  const { data: clientes } = useQuery({
    queryKey: ['clientes', 'search', buscarCliente],
    queryFn: () => clientesService.getClientes({ 
      search: buscarCliente,
      limit: 10,
      ativo: true
    }),
    enabled: buscarCliente.length >= 2
  })

  const { data: produtos } = useQuery({
    queryKey: ['produtos', 'search', buscarProduto],
    queryFn: () => produtosService.list({ 
      search: buscarProduto,
      limit: 10,
      ativo: true
    }),
    enabled: buscarProduto.length >= 2
  })

  const { data: vendasRecentes } = useQuery({
    queryKey: ['vendas', 'recentes'],
    queryFn: () => vendasService.list({ 
      limit: 20,
      page: 1
    }),
    enabled: showVendasRecentesModal
  })

  // Mutation para criar venda
  const criarVendaMutation = useMutation({
    mutationFn: (data: VendaFormData) => vendasService.create(data),
    onSuccess: (venda) => {
      success(`Venda ${venda.numero} criada com sucesso!`)
      queryClient.invalidateQueries({ queryKey: ['vendas'] })
      queryClient.invalidateQueries({ queryKey: ['produtos'] })
      limparVenda()
    },
    onError: (err: any) => {
      error('Erro ao criar venda', err.message)
    }
  })

  // Função para gerar PDF
  const gerarPDF = async (vendaId: number, tipo: 'recibo' | 'fatura') => {
    try {
      await apiService.generatePdf(vendaId, tipo)
      success(`${tipo === 'recibo' ? 'Recibo' : 'Fatura'} gerado com sucesso!`)
    } catch (err: any) {
      error(`Erro ao gerar ${tipo}`, err.message)
    }
  }

  // Handlers do carrinho
  const adicionarProdutoCarrinho = (produto: Produto) => {
    const itemExistente = carrinho.find(item => item.produtoId === produto.id)
    
    if (itemExistente) {
      // Incrementar quantidade
      const novaQuantidade = itemExistente.quantidade + 1
      if (novaQuantidade > produto.stockAtual) {
        error('Stock insuficiente', `Disponível: ${produto.stockAtual}`)
        return
      }
      
      setCarrinho(carrinho.map(item => 
        item.produtoId === produto.id 
          ? { ...item, quantidade: novaQuantidade }
          : item
      ))
    } else {
      // Adicionar novo item
      if (produto.stockAtual === 0) {
        error('Produto sem stock', 'Este produto não possui stock disponível')
        return
      }

      const novoItem: ItemCarrinho = {
        produtoId: produto.id,
        quantidade: 1,
        precoUnitario: produto.precoVenda,
        desconto: 0,
        produto,
        subtotal: produto.precoVenda,
        valorDesconto: 0,
        total: produto.precoVenda
      }
      
      setCarrinho([...carrinho, novoItem])
    }
    
    setBuscarProduto('')
  }

  const removerProdutoCarrinho = (produtoId: number) => {
    setCarrinho(carrinho.filter(item => item.produtoId !== produtoId))
  }

  const atualizarQuantidadeItem = (produtoId: number, quantidade: number) => {
    if (quantidade <= 0) {
      removerProdutoCarrinho(produtoId)
      return
    }

    const item = carrinho.find(item => item.produtoId === produtoId)
    if (item && quantidade > item.produto.stockAtual) {
      error('Stock insuficiente', `Disponível: ${item.produto.stockAtual}`)
      return
    }

    setCarrinho(carrinho.map(item => 
      item.produtoId === produtoId 
        ? { ...item, quantidade }
        : item
    ))
  }

  const atualizarDescontoItem = (produtoId: number, desconto: number) => {
    if (desconto < 0 || desconto > 100) return

    setCarrinho(carrinho.map(item => 
      item.produtoId === produtoId 
        ? { ...item, desconto }
        : item
    ))
  }

  // Calcular carrinho com totais em tempo real
  const carrinhoComTotais = useMemo(() => {
    return carrinho.map(item => {
      const subtotal = item.quantidade * item.precoUnitario
      const valorDesconto = (subtotal * item.desconto) / 100
      const total = subtotal - valorDesconto
      
      return {
        ...item,
        subtotal,
        valorDesconto,
        total
      }
    })
  }, [carrinho])

  // Calcular totais da venda
  const calcularTotais = () => {
    const subtotal = carrinhoComTotais.reduce((acc, item) => acc + item.subtotal, 0)
    const valorDescontoItens = carrinhoComTotais.reduce((acc, item) => acc + item.valorDesconto, 0)
    const subtotalComDescontoItens = subtotal - valorDescontoItens
    const valorDescontoGeral = (subtotalComDescontoItens * descontoGeral) / 100
    const subtotalFinal = subtotalComDescontoItens - valorDescontoGeral
    const valorIva = (subtotalFinal * 23) / 100
    const total = subtotalFinal + valorIva

    return {
      subtotal,
      valorDescontoItens,
      subtotalComDescontoItens,
      valorDescontoGeral,
      subtotalFinal,
      valorIva,
      total
    }
  }

  const totais = calcularTotais()

  // Handlers do cliente
  const selecionarCliente = (cliente: Cliente) => {
    setClienteSelecionado(cliente)
    setBuscarCliente('')
    setShowClienteModal(false)
  }

  const removerCliente = () => {
    setClienteSelecionado(null)
  }

  // Calcular valor recebido automaticamente dos pagamentos
  const valorRecebido = useMemo(() => {
    const total = pagamentos.reduce((acc, pagamento) => acc + pagamento.valor, 0)
    return total
  }, [pagamentos])

  // Handlers do pagamento
  const adicionarPagamento = (pagamento: PagamentoVendaFormData) => {
    const novosPagamentos = [...pagamentos, pagamento]
    setPagamentos(novosPagamentos)
    setShowPagamentoModal(false)

  }

  const removerPagamento = (index: number) => {
    setPagamentos(pagamentos.filter((_, i) => i !== index))
  }

  // Handler para finalizar venda
  const finalizarVenda = () => {
    if (carrinho.length === 0) {
      error('Carrinho vazio', 'Adicione pelo menos um produto ao carrinho')
      return
    }

    if (valorRecebido < totais.total - 0.01) {
      error('Pagamento insuficiente', 'O valor recebido é menor que o total da venda')
      return
    }

    const vendaData: VendaFormData = {
      clienteId: clienteSelecionado?.id || null,
      nomeCliente: clienteSelecionado?.nome || null,
      nifCliente: clienteSelecionado?.nif || null,
      emailCliente: clienteSelecionado?.email || null,
      telefoneCliente: clienteSelecionado?.telefonePrincipal || null,
      moradaCliente: clienteSelecionado?.morada || null,
      itens: carrinhoComTotais.map(item => ({
        produtoId: item.produtoId,
        quantidade: item.quantidade,
        precoUnitario: item.precoUnitario,
        desconto: item.desconto
      })),
      pagamentos,
      descontoGeral,
      observacoes,
      tipoDocumento
    }

    criarVendaMutation.mutate(vendaData)
  }

  const limparVenda = () => {
    setCarrinho([])
    setClienteSelecionado(null)
    setPagamentos([])
    setDescontoGeral(0)
    setObservacoes('')
    setBuscarCliente('')
    setBuscarProduto('')
    setShowPagamentoModal(false)
  }

  const troco = valorRecebido - totais.total

  // Handler para QR Scanner
  const handleQRScan = async (qrData: string) => {
    try {
      // Tentar fazer parse do QR code
      let produtoId: number | null = null
      
      try {
        const parsed = JSON.parse(qrData)
        if (parsed.type === 'produto' && parsed.id) {
          produtoId = parseInt(parsed.id)
        }
      } catch {
        // Se não for JSON, tentar como ID direto
        const id = parseInt(qrData)
        if (!isNaN(id)) {
          produtoId = id
        }
      }

      if (!produtoId) {
        error('QR Code inválido', 'O QR code escaneado não corresponde a um produto válido')
        return
      }

      // Buscar produto pelo ID
      const produto = await produtosService.getById(produtoId)
      if (produto) {
        adicionarProdutoCarrinho(produto)
        success(`Produto ${produto.nome} adicionado ao carrinho!`)
        setShowScannerModal(false)
      } else {
        error('Produto não encontrado', `Produto com ID ${produtoId} não foi encontrado`)
      }
    } catch (err: any) {
      error('Erro ao processar QR code', err.message || 'Erro desconhecido')
    }
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-3 lg:px-4 xl:px-6 py-1 lg:py-2 xl:py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ShoppingCart className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-base lg:text-lg xl:text-xl font-bold text-gray-900">Ponto de Venda</h1>
              <p className="text-xs text-gray-600 hidden xl:block">Sistema de vendas e recebimentos</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowVendasRecentesModal(true)}
              leftIcon={<History className="h-4 w-4" />}
            >
              <span className="hidden sm:inline">Vendas Recentes</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowScannerModal(true)}
              leftIcon={<QrCode className="h-4 w-4" />}
            >
              <span className="hidden sm:inline">Scanner</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={limparVenda}
              leftIcon={<X className="h-4 w-4" />}
            >
              <span className="hidden sm:inline">Limpar</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Área de produtos e busca */}
        <div className="flex-1 p-2 lg:p-3 xl:p-4 overflow-y-auto">
          {/* Busca de produtos */}
          <div className="mb-3 lg:mb-4">
            <Input
              placeholder="Buscar produtos..."
              value={buscarProduto}
              onChange={(e) => setBuscarProduto(e.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
            />
            
            {/* Resultados da busca */}
            {produtos && produtos.produtos.length > 0 && buscarProduto.length >= 2 && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {produtos.produtos.map((produto) => (
                  <div
                    key={produto.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => adicionarProdutoCarrinho(produto)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{produto.nome}</div>
                        <div className="text-sm text-gray-500">
                          SKU: {produto.codigoInterno} | Stock: {formatarQuantidade(produto.stockAtual)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">{formatarValor(produto.precoVenda)}</div>
                        <div className="text-sm text-gray-500">{produto.categoria?.nome || 'Sem categoria'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Carrinho */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-1 lg:p-2 xl:p-3 border-b border-gray-200">
              <h2 className="text-sm lg:text-base font-medium text-gray-900 flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Carrinho de Compras ({carrinho.length} itens)
              </h2>
            </div>
            
            <div className="divide-y divide-gray-100">
              {carrinho.length === 0 ? (
                <div className="p-4 lg:p-6 xl:p-8 text-center text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Carrinho vazio</p>
                  <p className="text-sm">Use a busca acima para adicionar produtos</p>
                </div>
              ) : (
                carrinhoComTotais.map((item) => (
                  <div key={item.produtoId} className="p-2 lg:p-3 xl:p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{item.produto.nome}</div>
                        <div className="text-sm text-gray-500">
                          SKU: {item.produto.codigoInterno} | {formatarValor(item.precoUnitario)} cada
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 lg:gap-2 flex-wrap justify-center lg:justify-start">
                        <div className="text-center">
                          <Input
                            type="number"
                            min="1"
                            max={item.produto.stockAtual}
                            value={item.quantidade}
                            onChange={(e) => atualizarQuantidadeItem(item.produtoId, parseInt(e.target.value) || 0)}
                            className="w-16 lg:w-20 text-center text-sm"
                          />
                          <div className="text-xs text-gray-400 mt-1">Qtd</div>
                        </div>

                        <div className="text-center">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={item.desconto}
                            onChange={(e) => atualizarDescontoItem(item.produtoId, parseFloat(e.target.value) || 0)}
                            className="w-16 lg:w-20 text-center text-sm"
                            placeholder="0"
                          />
                          <div className="text-xs text-gray-400 mt-1">% Desc</div>
                        </div>
                      </div>
                      
                      <div className="text-right min-w-[100px]">
                        <div className="font-medium text-gray-900">{formatarValor(item.total)}</div>
                        {item.valorDesconto > 0 && (
                          <div className="text-sm text-green-600">
                            -{formatarValor(item.valorDesconto)}
                          </div>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removerProdutoCarrinho(item.produtoId)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Resumo e pagamento */}
        <div className="w-full lg:w-72 xl:w-80 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col max-h-96 lg:max-h-none">
          {/* Cliente */}
          <div className="p-1 lg:p-2 xl:p-3 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between mb-1 lg:mb-2">
              <h3 className="text-xs lg:text-sm font-medium text-gray-900">Cliente</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowClienteModal(true)}
                leftIcon={<User className="h-4 w-4" />}
              >
                {clienteSelecionado ? 'Alterar' : 'Selecionar'}
              </Button>
            </div>
            
            {clienteSelecionado ? (
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-blue-900">{clienteSelecionado.nome}</div>
                    <div className="text-sm text-blue-700">
                      {clienteSelecionado.nif && `NIF: ${clienteSelecionado.nif}`}
                      {clienteSelecionado.telefonePrincipal && ` | ${clienteSelecionado.telefonePrincipal}`}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removerCliente}
                    className="text-blue-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 text-center py-2">
                Nenhum cliente selecionado
              </div>
            )}
          </div>

          {/* Resumo da venda */}
          <div className="p-1 lg:p-2 xl:p-3 bg-white border-b border-gray-200">
            <h3 className="text-xs lg:text-sm font-medium text-gray-900 mb-1 lg:mb-2">Resumo</h3>
            
            <div className="space-y-0.5 lg:space-y-1 text-xs lg:text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span>{formatarValor(totais.subtotal)}</span>
              </div>
              
              {totais.valorDescontoItens > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto itens:</span>
                  <span>-{formatarValor(totais.valorDescontoItens)}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-xs lg:text-sm">Desconto geral:</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={descontoGeral}
                    onChange={(e) => setDescontoGeral(parseFloat(e.target.value) || 0)}
                    className="w-12 lg:w-14 text-xs"
                    placeholder="%"
                  />
                  <span className="text-green-600 text-xs lg:text-sm">-{formatarValor(totais.valorDescontoGeral)}</span>
                </div>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">IVA (23%):</span>
                <span>{formatarValor(totais.valorIva)}</span>
              </div>
              
              <hr />
              
              <div className="flex justify-between font-medium text-sm lg:text-base">
                <span>Total:</span>
                <span>{formatarValor(totais.total)}</span>
              </div>
            </div>
          </div>

          {/* Tipo de documento */}
          <div className="p-1 lg:p-2 xl:p-3 bg-white border-b border-gray-200">
            <Select
              label="Tipo de Documento"
              value={tipoDocumento}
              onChange={(e) => setTipoDocumento(e.target.value as 'RECIBO' | 'FATURA')}
              options={tiposDocumento.map(tipo => ({
                value: tipo.value,
                label: tipo.label
              }))}
            />
          </div>

          {/* Pagamentos */}
          <div className="flex-1 p-1 lg:p-2 xl:p-3 bg-white overflow-y-auto">
            <div className="flex items-center justify-between mb-1 lg:mb-2">
              <h3 className="text-xs lg:text-sm font-medium text-gray-900">Pagamentos</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPagamentoModal(true)}
                leftIcon={<CreditCard className="h-4 w-4" />}
                disabled={carrinho.length === 0}
              >
                Adicionar
              </Button>
            </div>
            
            <div className="space-y-2 mb-4">
              {pagamentos.map((pagamento, index) => {
                const config = getFormaPagamentoConfig(pagamento.formaPagamento)
                return (
                  <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200">
                    <div className="flex items-center gap-2">
                      <span>{config.icon}</span>
                      <div>
                        <div className="text-sm font-medium text-green-900">{config.label}</div>
                        {pagamento.referencia && (
                          <div className="text-xs text-green-700">{pagamento.referencia}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-green-900">{formatarValor(pagamento.valor)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removerPagamento(index)}
                        className="text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
            
            {pagamentos.length > 0 && (
              <div className="space-y-2 text-sm border-t pt-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Valor recebido:</span>
                  <span className="font-medium">{formatarValor(valorRecebido)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Valor da venda:</span>
                  <span className="font-medium">{formatarValor(totais.total)}</span>
                </div>
                <div className={`flex justify-between font-medium ${troco >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <span>{troco >= 0 ? 'Troco:' : 'Pendente:'}</span>
                  <span>{formatarValor(Math.abs(troco))}</span>
                </div>
              </div>
            )}
          </div>

          {/* Botão finalizar */}
          <div className="p-1 lg:p-2 xl:p-3 bg-white border-t border-gray-200">
            <Button
              fullWidth
              size="lg"
              onClick={finalizarVenda}
              disabled={carrinho.length === 0 || troco < -0.01 || criarVendaMutation.isPending}
              isLoading={criarVendaMutation.isPending}
              leftIcon={<Receipt className="h-5 w-5" />}
            >
              {troco < -0.01 ? 'Pagamento Pendente' : 'Finalizar Venda'}
            </Button>
          </div>
        </div>
      </div>

      {/* Modal de seleção de cliente */}
      <Modal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        title="Selecionar Cliente"
        size="md"
      >
        <div className="space-y-4">
          <Input
            placeholder="Buscar cliente por nome, NIF ou telefone..."
            value={buscarCliente}
            onChange={(e) => setBuscarCliente(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
          
          {clientes && clientes.data.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded">
              {clientes.data.map((cliente) => (
                <div
                  key={cliente.id}
                  className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  onClick={() => selecionarCliente(cliente)}
                >
                  <div className="font-medium text-gray-900">{cliente.nome}</div>
                  <div className="text-sm text-gray-500">
                    {cliente.nif && `NIF: ${cliente.nif}`}
                    {cliente.telefonePrincipal && ` | ${cliente.telefonePrincipal}`}
                    {cliente.email && ` | ${cliente.email}`}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowClienteModal(false)}>
              Cancelar
            </Button>
            <Button onClick={() => {
              setClienteSelecionado(null)
              setShowClienteModal(false)
            }}>
              Venda sem Cliente
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de vendas recentes */}
      <Modal
        isOpen={showVendasRecentesModal}
        onClose={() => setShowVendasRecentesModal(false)}
        title="Vendas Recentes"
        size="lg"
      >
        <div className="space-y-4">
          {vendasRecentes && vendasRecentes.vendas.length > 0 ? (
            <div className="space-y-3">
              {vendasRecentes.vendas.map((venda: any) => (
                <div
                  key={venda.id}
                  className="border border-gray-200 rounded-lg p-4 bg-white"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {venda.tipoDocumento} {venda.numero}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          venda.tipoDocumento === 'RECIBO' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {venda.tipoDocumento}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {new Date(venda.createdAt).toLocaleString('pt-PT')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-gray-900">
                        {formatarValor(venda.total)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {venda.cliente?.nome || 'Cliente não identificado'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => gerarPDF(venda.id, 'recibo')}
                      leftIcon={<Receipt className="h-4 w-4" />}
                    >
                      Recibo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => gerarPDF(venda.id, 'fatura')}
                      leftIcon={<FileText className="h-4 w-4" />}
                    >
                      Fatura
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => gerarPDF(venda.id, venda.tipoDocumento.toLowerCase() as 'recibo' | 'fatura')}
                      leftIcon={<Download className="h-4 w-4" />}
                    >
                      Download Original
                    </Button>
                  </div>

                  {venda.itens && venda.itens.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-sm text-gray-600">
                        <strong>Itens:</strong> {venda.itens.map((item: any) => 
                          `${item.quantidade}x ${item.produto?.nome || 'Produto'}`
                        ).join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nenhuma venda encontrada</p>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowVendasRecentesModal(false)}
            >
              Fechar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de pagamento */}
      <Modal
        isOpen={showPagamentoModal}
        onClose={() => setShowPagamentoModal(false)}
        title="Adicionar Pagamento"
        size="md"
      >
        <PagamentoModal
          valorPendente={totais.total - valorRecebido}
          onAddPagamento={adicionarPagamento}
          onCancel={() => setShowPagamentoModal(false)}
        />
      </Modal>

      {/* Modal QR Scanner */}
      <QRScanner
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onScan={handleQRScan}
        title="Escanear Produto"
      />
      
    </div>
  )
}

export default VendasPage